//frontend/src/components/Auth/Login.jsx

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth';
import { Form, Button, Container, Card, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { useGoogleLogin } from '@react-oauth/google';
import { 
    FaUser, 
    FaLock, 
    FaEye, 
    FaEyeSlash, 
    FaShieldAlt,
    FaHeadset,
    FaBolt,
    FaCheckCircle,
    FaChartLine,
    FaUsers,
    FaClock
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Auth.css';

const Login = () => {
    const [credentials, setCredentials] = useState({
        username: '',
        password: '',
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const { login, setUser } = useAuth();
    const navigate = useNavigate();

    const handleGoogleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            setLoading(true);
            setError('');
            try {
                await authService.googleLogin(tokenResponse.access_token);
                // Fetch fresh user data and update AuthContext
                const userData = await authService.getCurrentUser();
                setUser(userData);
                toast.success('✅ Connexion Google réussie ! Bienvenue.');
                if (userData.role === 'superadmin') {
                    navigate('/superadmin');
                } else if (userData.role === 'admin') {
                    navigate('/admin');
                } else if ((userData.profile_completion_percentage || 0) < 80) {
                    navigate('/profile/complete');
                } else {
                    navigate('/dashboard');
                }
            } catch (err) {
                setError('Échec de la connexion avec Google. Veuillez réessayer.');
                toast.error('❌ Connexion Google échouée.');
            } finally {
                setLoading(false);
            }
        },
        onError: () => {
            setError('Connexion Google annulée ou échouée.');
            toast.error('❌ Connexion Google échouée.');
        },
    });

    const handleChange = (e) => {
        setCredentials({
            ...credentials,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(credentials);
            
            // Récupérer les infos de l'utilisateur après connexion
            const userData = await authService.getCurrentUser();
            
            // NOUVEAU: Vérifier si l'email est vérifié
            if (!userData.email_verified) {
                setError('Veuillez vérifier votre email avant de vous connecter. Vérifiez votre boîte mail.');
                toast.warning('⚠️ Email non vérifié. Vérifiez votre boîte mail.');
                setLoading(false);
                return;
            }
            
            toast.success('✅ Connexion réussie ! Bienvenue.');
            
            // Redirection selon le rôle
            if (userData.role === 'superadmin') {
                navigate('/superadmin');
            } else if (userData.role === 'admin') {
                navigate('/admin');
            } else if ((userData.profile_completion_percentage || 0) < 80) {
                navigate('/profile/complete');
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            setError('Identifiants invalides. Veuillez réessayer.');
            toast.error('❌ Identifiants incorrects.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <Container>
                <Row className="justify-content-center py-5">
                    <Col lg={8} xl={10}>
                        <Card className="auth-card shadow-lg">
                            <Row className="g-0">
                                {/* Left Side - Info */}
                                <Col md={6} className="auth-info-section">
                                    <div className="auth-info-content">
                                        <div className="auth-logo">
                                            <h1>🍁 E-Visa Canada</h1>
                                        </div>
                                        <h2 className="auth-info-title">
                                            Bienvenue sur votre espace personnel
                                        </h2>
                                        <p className="auth-info-text">
                                            Accédez à votre tableau de bord pour suivre vos demandes 
                                            de visa et communiquer avec nos conseillers.
                                        </p>
                                        
                                        <div className="auth-features">
                                            <div className="auth-feature">
                                                <FaCheckCircle className="feature-icon" />
                                                <span>Suivi en temps réel de vos dossiers</span>
                                            </div>
                                            <div className="auth-feature">
                                                <FaHeadset className="feature-icon" />
                                                <span>Support client 24/7</span>
                                            </div>
                                            <div className="auth-feature">
                                                <FaShieldAlt className="feature-icon" />
                                                <span>Sécurité maximale de vos données</span>
                                            </div>
                                        </div>

                                        <div className="auth-stats">
                                            <div className="stat-item">
                                                <FaUsers className="stat-icon-svg" />
                                                <h3>15,000+</h3>
                                                <p>Visas approuvés</p>
                                            </div>
                                            <div className="stat-item">
                                                <FaChartLine className="stat-icon-svg" />
                                                <h3>92%</h3>
                                                <p>Taux de succès</p>
                                            </div>
                                            <div className="stat-item">
                                                <FaClock className="stat-icon-svg" />
                                                <h3>4-6 sem</h3>
                                                <p>Délai moyen</p>
                                            </div>
                                        </div>

                                        <div className="trust-badges">
                                            <Container>
                                                <Row className="text-center">
                                                    <Col>
                                                        <div className="trust-badge-item">
                                                            <FaLock className="trust-icon-svg" />
                                                            <p>Paiement sécurisé</p>
                                                        </div>
                                                    </Col>
                                                    <Col>
                                                        <div className="trust-badge-item">
                                                            <FaCheckCircle className="trust-icon-svg" />
                                                            <p>Certifié ISO</p>
                                                        </div>
                                                    </Col>
                                                    <Col>
                                                        <div className="trust-badge-item">
                                                            <FaShieldAlt className="trust-icon-svg" />
                                                            <p>Données protégées</p>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Container>
                                        </div>
                                    </div>
                                </Col>

                                {/* Right Side - Form */}
                                <Col md={6} className="auth-form-section">
                                    <div className="auth-form-content">
                                        <h3 className="auth-form-title">Connexion</h3>
                                        <p className="auth-form-subtitle">
                                            Connectez-vous pour accéder à votre espace
                                        </p>

                                        <div className="auth-social-buttons mb-3">
                                            <button className="btn-google" onClick={() => handleGoogleLogin()} disabled={loading} type="button">
                                                <span className="btn-google-icon">
                                                    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                                                        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                                                        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                                                        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                                                    </svg>
                                                </span>
                                                <span className="btn-google-text">Continuer avec Google</span>
                                            </button>
                                        </div>

                                        <div className="auth-divider">
                                            <span>OU</span>
                                        </div>

                                        {error && (
                                            <Alert variant="danger" className="auth-alert">
                                                <strong>Erreur :</strong> {error}
                                            </Alert>
                                        )}

                                        {error && error.includes('email') && (
                                            <div className="text-center mb-3">
                                                <p className="text-muted mb-2">Vous n'avez pas reçu l'email de vérification ?</p>
                                                <Link to="/resend-verification" className="btn btn-outline-primary">
                                                    Renvoyer l'email de vérification
                                                </Link>
                                            </div>
                                        )}
                                                                                
                                        <Form onSubmit={handleSubmit} className="auth-form">
                                            <Form.Group className="mb-4">
                                                <Form.Label className="form-label-custom">
                                                    Nom d'utilisateur ou email
                                                </Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text className="input-icon-group">
                                                        <FaUser />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="text"
                                                        name="username"
                                                        value={credentials.username}
                                                        onChange={handleChange}
                                                        required
                                                        className="form-control-custom"
                                                        placeholder="Entrez votre identifiant"
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-4">
                                                <Form.Label className="form-label-custom">
                                                    Mot de passe
                                                </Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text className="input-icon-group">
                                                        <FaLock />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type={showPassword ? "text" : "password"}
                                                        name="password"
                                                        value={credentials.password}
                                                        onChange={handleChange}
                                                        required
                                                        className="form-control-custom"
                                                        placeholder="Entrez votre mot de passe"
                                                    />
                                                    <InputGroup.Text 
                                                        className="input-icon-group password-toggle"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>

                                            <div className="form-options mb-4">
                                                <Form.Check 
                                                    type="checkbox" 
                                                    label="Se souvenir de moi"
                                                    className="remember-me"
                                                />
                                                <Link to="/forgot-password" className="forgot-password">
                                                    Mot de passe oublié ?
                                                </Link>
                                            </div>

                                            <Button
                                                type="submit"
                                                className="btn-auth-submit w-100"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Connexion en cours...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaBolt className="me-2" />
                                                        Se connecter
                                                    </>
                                                )}
                                            </Button>
                                        </Form>

                                        <div className="auth-footer">
                                            <p>
                                                Vous n'avez pas de compte ?{' '}
                                                <Link to="/register" className="auth-link">
                                                    Inscrivez-vous gratuitement
                                                </Link>
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Login;